import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Send, Image as ImageIcon, ArrowLeft, MessageCircle, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function DMPage() {
  const { userId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [otherUser, setOtherUser] = useState(null);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    if (userId) openConvo(userId);
  }, [userId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchConversations = async () => {
    try { const res = await axios.get(`${API}/dm/conversations`, { withCredentials: true }); setConversations(res.data || []); } catch {}
  };

  const openConvo = async (targetUserId) => {
    const convoId = [user.user_id, targetUserId].sort().join("_");
    setActiveConvo(convoId);
    try {
      const [msgRes, userRes] = await Promise.all([
        axios.get(`${API}/dm/${convoId}`, { withCredentials: true }),
        axios.get(`${API}/users/profile/${targetUserId}`)
      ]);
      setMessages(msgRes.data || []);
      setOtherUser(userRes.data);
    } catch {}
  };

  const sendMessage = async () => {
    if (!input.trim() && !file) return;
    const targetId = userId || otherUser?.user_id;
    if (!targetId) return;
    setSending(true);
    const fd = new FormData();
    fd.append("content", input);
    fd.append("to_user_id", targetId);
    if (file) fd.append("file", file);
    try {
      await axios.post(`${API}/dm`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setInput(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      openConvo(targetId);
      fetchConversations();
    } catch { toast.error("Failed to send"); }
    finally { setSending(false); }
  };

  const getAvatarUrl = (pic) => pic ? (pic.startsWith('/api') ? `${API.replace('/api', '')}${pic}` : pic) : null;

  // Conversation list view
  if (!userId && !activeConvo) {
    return (
      <Layout>
        <section className="gradient-hero py-8"><div className="container-custom"><h1 className="text-heading-1 text-white mb-2">Messages</h1><p className="text-white/80">Direct messages with other members</p></div></section>
        <section className="section-spacing">
          <div className="container-custom max-w-2xl">
            {conversations.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border"><MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-slate-600">No messages yet</h3><p className="text-slate-400 mt-1">Visit someone's profile and click "Message" to start a conversation</p></div>
            ) : (
              <div className="space-y-2">
                {conversations.map(c => (
                  <Link key={c.convo_id} to={`/dm/${c.other_user.user_id}`} className="flex items-center gap-3 p-4 bg-white rounded-xl border hover:shadow-sm transition-shadow">
                    <div className="w-12 h-12 rounded-full bg-deep-blue flex items-center justify-center text-white font-bold overflow-hidden shrink-0">
                      {getAvatarUrl(c.other_user.picture) ? <img src={getAvatarUrl(c.other_user.picture)} alt="" className="w-full h-full object-cover" /> : c.other_user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-deep-blue">{c.other_user.name}</span>
                        <span className="text-xs text-slate-400">{c.last_time ? formatDistanceToNow(new Date(c.last_time), { addSuffix: true }) : ""}</span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">{c.last_message}</p>
                    </div>
                    {c.unread > 0 && <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{c.unread}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </Layout>
    );
  }

  // Chat view
  return (
    <Layout>
      <div className="container-custom max-w-2xl py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b">
          <Link to="/dm"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button></Link>
          {otherUser && (
            <Link to={`/profile/${otherUser.user_id}`} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-deep-blue flex items-center justify-center text-white font-bold overflow-hidden">
                {getAvatarUrl(otherUser.picture) ? <img src={getAvatarUrl(otherUser.picture)} alt="" className="w-full h-full object-cover" /> : otherUser.name?.charAt(0)?.toUpperCase()}
              </div>
              <span className="font-semibold text-deep-blue">{otherUser.name}</span>
            </Link>
          )}
        </div>

        {/* Messages */}
        <div className="h-[calc(100vh-280px)] overflow-y-auto space-y-3 mb-4">
          {messages.map(m => (
            <div key={m.dm_id} className={`flex ${m.from_id === user.user_id ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.from_id === user.user_id ? 'bg-deep-blue text-white rounded-br-md' : 'bg-slate-100 text-slate-800 rounded-bl-md'}`}>
                {m.media?.url && <img src={`${API.replace('/api', '')}${m.media.url}`} alt="" className="max-h-48 rounded-lg mb-2" />}
                {m.content && <p className="text-sm">{m.content}</p>}
                <p className={`text-[10px] mt-1 ${m.from_id === user.user_id ? 'text-white/50' : 'text-slate-400'}`}>
                  {m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true }) : ""}
                </p>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>

        {/* Input */}
        {file && (
          <div className="flex items-center gap-2 mb-2 bg-slate-50 p-2 rounded-lg">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 flex-1">{file.name}</span>
            <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}><X className="w-4 h-4 text-slate-400" /></button>
          </div>
        )}
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="shrink-0"><ImageIcon className="w-4 h-4" /></Button>
          <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Type a message..." />
          <Button onClick={sendMessage} disabled={sending || (!input.trim() && !file)} className="bg-deep-blue text-white shrink-0"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </Layout>
  );
}
